import { useSupplyUiConfig } from '../invoice-fields'
import React, { useRef, useState } from 'react'
import { Alert, Button, Checkbox, DatePicker, Form, Modal, Progress, Space, Tag, Typography, Upload } from 'antd'
import { DeleteOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons'
import filesize from 'filesize'
import dayjs, { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { Delivery, DeliveryAnalysis, canReceiveDelivery, isDeliveryAccepted } from './model'
import { supplyRequest, uploadDeliveryFile } from './api'
import { analysisPath } from './analysis'
import SupplierFiles from './supplier-files'

export default function DeliveryIntakeModal({
  delivery,
  onClose,
  onStarted,
  resume = false,
}: {
  delivery: Delivery
  onClose: () => void
  onStarted: (analysis: DeliveryAnalysis) => void
  resume?: boolean
}) {
  const { can_open_chat: canOpenChat } = useSupplyUiConfig()
  const navigate = useNavigate()
  const latest = delivery.intakes[0]
  const group = delivery.deliveries || [delivery]
  const available = group.filter(canReceiveDelivery)
  const [selectedKeys, setSelectedKeys] = useState(available.length <= 100 ? available.map(row => row.key) : [])
  const [arrivedAt, setArrivedAt] = useState<Dayjs | null>(
    resume && latest ? dayjs(latest.arrived_at) : dayjs(),
  )
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState('')
  const [error, setError] = useState('')
  const [progress, setProgress] = useState<Record<string, number>>({})
  const [newIntake, setNewIntake] = useState(resume || delivery.uploadMode || !delivery.intakes.length)
  const [reviewId, setReviewId] = useState(resume && latest ? latest.review_id : '')
  const intakeRef = useRef<any>(resume ? latest : undefined)
  const chatRef = useRef('')
  const clientId = useRef(resume && latest?.client_id ? latest.client_id : uuid())
  const attachments = useRef(new Map<File, any>())
  const attempts = useRef(new Map<File, string>())
  const openReview = (id: string, documentId?: string) =>
    navigate(analysisPath(id, documentId ? { status: 'review', documentId } : undefined))

  const submit = async () => {
    if (busy || !arrivedAt || !files.length || !selectedKeys.length || selectedKeys.some(key => !available.some(row => row.key === key))) return
    setBusy(true)
    setError('')
    try {
      setStep('Подготавливаем приёмку…')
      if (!intakeRef.current) {
        intakeRef.current = (
          await supplyRequest('/api/supply/deliveries/intakes', {
            method: 'POST',
            timeoutMs: 180000,
            body: JSON.stringify({
              client_id: clientId.current,
              delivery_keys: selectedKeys,
              arrived_at: arrivedAt.toISOString(),
            }),
          })
        ).data
        setReviewId(intakeRef.current.review_id)
      }
      const targetId = intakeRef.current.review_id
      if (!chatRef.current) {
        const existing = await supplyRequest(`/api/supply/reviews/${targetId}`)
        chatRef.current = existing.data?.chat_id || ''
        if (!chatRef.current) {
          const purpose = await supplyRequest('/api/supply/review-agent')
          if (!purpose.data?.purpose_id) throw new Error('Агент разбора документов недоступен')
          const created = await supplyRequest(
            `/api/mcp2/chat-purpose-options/${encodeURIComponent(
              purpose.data.purpose_id,
            )}/chats?embedded=1`,
            {
              method: 'POST',
              timeoutMs: 180000,
              body: JSON.stringify({
                name: `Поставка · ${delivery.supplier || delivery.document_number}`,
                auto_title: true,
              }),
            },
          )
          if (!created.data?.id) throw new Error('Не удалось создать чат разбора')
          chatRef.current = created.data.id
        }
      }
      await supplyRequest(`/api/supply/reviews/${targetId}/chat`, {
        method: 'PUT',
        body: JSON.stringify({ chat_id: chatRef.current }),
      })
      for (const [index, file] of files.entries()) {
        if (attachments.current.has(file)) {
          setProgress(previous => ({ ...previous, [String(index)]: 100 }))
          continue
        }
        setStep(`Загружаем документ ${index + 1} из ${files.length}: ${file.name}`)
        if (!attempts.current.has(file)) attempts.current.set(file, uuid())
        const uploaded = await uploadDeliveryFile(
          file,
          chatRef.current,
          attempts.current.get(file)!,
          percent => setProgress(previous => ({ ...previous, [String(index)]: percent })),
        )
        attachments.current.set(file, uploaded)
      }
      setStep('Отправляем документы на разбор…')
      await supplyRequest(`/api/supply/processing/reviews/${targetId}/enqueue`, {
        method: 'POST', body: JSON.stringify({ attachments: files.map(file => attachments.current.get(file)) }),
      })
      onStarted({
        ...intakeRef.current,
        chat_id: chatRef.current,
        supplier: delivery.supplier,
        document_number: delivery.document_number,
        status: 'processing',
        document_count: 0,
        checked_count: 0,
      })
    } catch (cause: any) {
      setError(cause.message || 'Не удалось начать разбор. Повторите попытку')
    } finally {
      setBusy(false)
      setStep('')
    }
  }

  return (
    <Modal
      open
      className="delivery-intake"
      title="Принять поставку"
      width={620}
      onCancel={onClose}
      closable={!busy}
      maskClosable={!busy}
      keyboard={!busy}
      footer={
        <Space>
          <Button disabled={busy} onClick={onClose}>
            Закрыть
          </Button>
          {newIntake && (
            <Button
              type="primary"
              loading={busy}
              disabled={!arrivedAt || !files.length || !selectedKeys.length || !available.length}
              onClick={submit}
            >
              {error ? 'Повторить отправку' : 'Начать разбор'}
            </Button>
          )}
        </Space>
      }
    >
      <div className="delivery-intake__heading">
        <Typography.Text strong>{delivery.supplier || 'Поставщик не указан'}</Typography.Text>
        <Typography.Text type="secondary">
          {delivery.deliveries ? `Документов: ${delivery.deliveries.length} · позиций: ${delivery.deliveries.reduce((sum, row) => sum + row.position_count, 0)}` : `${delivery.dataset === 'orders' ? 'Заказ поставщику' : 'Заявка на закупку'} № ${delivery.document_number} · позиций: ${delivery.position_count}`}
        </Typography.Text>
      </div>
      {!!delivery.deliveries?.length && <div className="delivery-intake__orders">
        <Typography.Text type="secondary">Можно загрузить один УПД на несколько заказов или заявок. Несопоставленные документы останутся ожидаемыми.</Typography.Text>
        {newIntake ? <>
          <Checkbox checked={available.length > 0 && selectedKeys.length === available.length} indeterminate={selectedKeys.length > 0 && selectedKeys.length < available.length} disabled={busy || !!reviewId || !available.length || available.length > 100}
            onChange={event => setSelectedKeys(event.target.checked ? available.map(row => row.key) : [])}>Все ожидаемые документы ({available.length})</Checkbox>
          {available.length > 100 && <Alert type="info" message="Выберите до 100 документов для одного разбора" />}
          <Checkbox.Group value={selectedKeys} disabled={busy || !!reviewId} onChange={keys => { if (keys.length <= 100) setSelectedKeys(keys.map(String)) }} className="delivery-intake__order-selection">
            {group.map(row => <Checkbox key={row.key} value={row.key} disabled={!canReceiveDelivery(row)}>{row.dataset === 'orders' ? 'Заказ' : 'Заявка'} № {row.document_number} · позиций: {row.position_count}{isDeliveryAccepted(row) && <Tag color="green">Уже принято</Tag>}</Checkbox>)}
          </Checkbox.Group>
        </> : <div>{group.map(row => <span key={row.key}>{row.dataset === 'orders' ? 'Заказ' : 'Заявка'} № {row.document_number}</span>)}</div>}
      </div>}
      {!!delivery.intakes.length && <SupplierFiles analyses={delivery.intakes.map(row => ({ ...row, delivery_key: delivery.key,
        supplier: delivery.supplier, document_number: delivery.document_number, created_at: row.created_at || '', chat_id: null }))} refresh={onClose} />}
      {!newIntake && latest && (
        <div className="delivery-intake__existing">
          <Alert
            type={latest.status === 'checked' ? 'success' : 'info'}
            showIcon
            message={
              latest.status === 'checked'
                ? 'Документы предыдущего поступления проверены'
                : latest.document_count
                ? 'Накладные готовы к проверке'
                : 'Документы разбираются в фоне'
            }
            description={`Поступление ${dayjs(latest.arrived_at).format('DD.MM.YYYY HH:mm')}${
              latest.document_count
                ? ` · проверено ${latest.checked_count} из ${latest.document_count} накладных`
                : '. Можно закрыть окно и принять следующую поставку.'
            }`}
          />
          <Space wrap>
            <Button
              type={latest.document_count ? 'primary' : 'default'}
              disabled={!canOpenChat && !latest?.document_count} onClick={() => openReview(latest.review_id, latest.document_id)}
            >
              {latest.document_count ? 'Проверить накладные' : canOpenChat ? 'Открыть чат' : 'Разбор выполняется'}
            </Button>
            {!!available.length && (
              <Button onClick={() => setNewIntake(true)}>Добавить документы</Button>
            )}
          </Space>
          {delivery.intakes.slice(1).map(intake => (
            <Button
              key={intake.id}
              block
              disabled={!canOpenChat && !intake.document_count} onClick={() => openReview(intake.review_id, intake.document_id)}
            >
              Поступление {dayjs(intake.arrived_at).format('DD.MM.YYYY HH:mm')} ·{' '}
              {intake.status === 'checked' ? 'проверено' : 'в работе'}
            </Button>
          ))}
        </div>
      )}
      {newIntake && (
        <Form layout="vertical">
          <Form.Item label="Дата поступления · можно уточнить при подтверждении" required>
            <DatePicker
              value={arrivedAt}
              onChange={setArrivedAt}
              showTime={{ format: 'HH:mm' }}
              format="DD.MM.YYYY HH:mm"
              disabled={busy || !!reviewId}
              disabledDate={date => date.startOf('day').isAfter(dayjs().startOf('day'))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="Документы поставки"
            required
            extra="После загрузки разбор продолжится в фоне. Вы сможете сразу принять следующую поставку, а результат появится в календаре."
          >
            <div className="delivery-intake__documents">
              <Upload.Dragger
                className="delivery-intake__upload"
                multiple
                disabled={busy}
                showUploadList={false}
                beforeUpload={file => {
                  if (!file.size) {
                    setError('Файл пуст. Выберите документ с содержимым')
                    return Upload.LIST_IGNORE
                  }
                  setFiles(previous => [...previous, file])
                  return false
                }}
                fileList={files.map((file, index) => ({
                  uid: String(index),
                  name: file.name,
                  size: file.size,
                  status: progress[String(index)] === 100 ? ('done' as const) : undefined,
                }))}
              >
                <div className="delivery-intake__dropzone">
                  <InboxOutlined className="delivery-intake__dropzone-icon" />
                  <div>
                    <div className="delivery-intake__dropzone-title">
                      {files.length ? 'Добавить ещё документы' : 'Перетащите документы сюда'}
                    </div>
                    <div className="delivery-intake__dropzone-action">или выберите файлы</div>
                    <div className="delivery-intake__dropzone-hint">
                      PDF, сканы, фотографии и документы ЭДО
                    </div>
                  </div>
                </div>
              </Upload.Dragger>
              {!!files.length && (
                <div className="delivery-intake__attachments">
                  <Typography.Text type="secondary">
                    Прикреплено документов: {files.length}
                  </Typography.Text>
                  <ul className="delivery-intake__file-list" aria-label="Прикреплённые документы">
                    {files.map((file, index) => {
                      const percent = progress[String(index)]
                      return (
                        <li key={index} className="delivery-intake__file">
                          <FileOutlined className="delivery-intake__file-icon" />
                          <div className="delivery-intake__file-info">
                            <div className="delivery-intake__file-name" title={file.name}>
                              {file.name}
                            </div>
                            <div className="delivery-intake__file-meta">
                              {filesize(file.size, { round: 1 })}
                              {percent === 100 && <span> · Загружен</span>}
                            </div>
                            {busy && percent != null && percent < 100 && (
                              <Progress percent={percent} size="small" />
                            )}
                          </div>
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            disabled={busy}
                            aria-label={`Удалить документ ${file.name}`}
                            title="Удалить документ"
                            onClick={() => {
                              setFiles(previous =>
                                previous.filter((_, fileIndex) => fileIndex !== index),
                              )
                              setProgress({})
                            }}
                          />
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </div>
          </Form.Item>
          {busy && (
            <div aria-live="polite">
              <Typography.Text>{step}</Typography.Text>
              <Progress
                percent={Math.round(
                  files.reduce((sum, _, index) => sum + (progress[String(index)] || 0), 0) /
                    files.length,
                )}
              />
            </div>
          )}
          {error && (
            <Alert
              showIcon
              type="error"
              message={error}
              action={
                reviewId && canOpenChat ? (
                  <Button size="small" disabled={!canOpenChat && !latest?.document_count} onClick={() => openReview(reviewId)}>
                    Открыть разбор
                  </Button>
                ) : undefined
              }
            />
          )}
          <Typography.Paragraph type="secondary" className="delivery-intake__note">
            После разбора проверьте данные накладных и подтвердите приёмку.
          </Typography.Paragraph>
        </Form>
      )}
    </Modal>
  )
}
